import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import {
    getNewAppUrlForCurrentPath,
    shouldSkipNewBuyerAppRedirect,
} from "@/utils";
import { sentryCaptureException } from "@/utils/error-handling/sentryCaptureException";
import UserService from "@/services/User";
import { useAuthStore } from "@/stores/userAuth";

const MINT_TIMEOUT_MS = 5000;

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        const timer = setTimeout(
            () => reject(new Error(`Timed out after ${ms}ms`)),
            ms
        );
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (err) => {
                clearTimeout(timer);
                reject(err);
            }
        );
    });

export const useBrandRedirect = () => {
    const authStore = useAuthStore();
    const route = useRoute();

    // True once we know this app must keep rendering for the brand user:
    // the dev escape hatch is set, or no new-app URL is configured.
    const redirectUnavailable = ref(false);
    const hasRedirected = ref(false);

    const isOnProtectedRoute = computed(() => route.meta.requiresAuth === true);

    const isAuthenticatedBrand = computed(
        () =>
            authStore.auth &&
            authStore.loaded &&
            !authStore.masqueradeMode &&
            authStore.isBrand
    );

    const performRedirect = async () => {
        hasRedirected.value = true;

        let tempToken: string | undefined;
        try {
            const response = await withTimeout(
                UserService.getInstance().mintSsoTemporaryToken(),
                MINT_TIMEOUT_MS
            );
            tempToken = response.data?.token;
        } catch (err) {
            // Mint failures (and timeouts) must not block the redirect — the
            // user falls back to logging in on the destination instead of
            // sitting on a blocked page. Capture silently: a toast right
            // before a hard navigation is confusing, and the redirect itself
            // is the recovery path.
            sentryCaptureException(err);
        }

        // Re-check after the await: the user may have logged out, entered
        // masquerade, or moved to a public route while the mint was pending.
        // Bouncing them now would be wrong, so stand down and let the
        // watcher re-evaluate if they become eligible again.
        if (!isOnProtectedRoute.value || !isAuthenticatedBrand.value) {
            hasRedirected.value = false;
            return;
        }

        const target = getNewAppUrlForCurrentPath(tempToken);
        if (!target) {
            // No destination configured (missing env in a non-dev build).
            // Unblock the current app rather than leaving the user on a
            // blank page — the redirect is unavailable here.
            hasRedirected.value = false;
            redirectUnavailable.value = true;
            return;
        }
        window.location.replace(target);
    };

    // Brands always live in the new buyer app now (the rollout flag was
    // folded in); any authenticated brand landing on a protected route here
    // is bounced over.
    watch(
        [isOnProtectedRoute, isAuthenticatedBrand],
        ([protectedRoute, authedBrand]) => {
            if (hasRedirected.value) return;
            if (!protectedRoute || !authedBrand) return;

            // Engineering escape hatch: stay in this app so we can exercise
            // the legacy paths during testing.
            if (shouldSkipNewBuyerAppRedirect()) {
                redirectUnavailable.value = true;
                return;
            }

            performRedirect();
        },
        { immediate: true }
    );

    const shouldBlockRender = computed(() => {
        if (!isOnProtectedRoute.value) return false;
        if (!isAuthenticatedBrand.value) return false;
        return !redirectUnavailable.value || hasRedirected.value;
    });

    return {
        shouldBlockRender,
    };
};
